import { createContext, useContext, ReactNode, useState, useEffect, useRef } from 'react';

import { ResponseStatus } from '@revenuemarket/http';
import { getCookie } from 'cookies-next';
import { useRouter } from 'next/router';

import { verifySuperToken } from '~apis/auth/signin';
import { AuthQueryKey } from '~hooks/queries/auth/query';
import { UserCompaniesQueryKey, useUserCompanies } from '~hooks/queries/users/query';
import { getDualStorageValue, setDualStorageValue } from '~hooks/useDualStorage';
import useSuperUserHook from '~hooks/useSuperUserHook';
import { queryClient } from '~libs/queryClient';

export const COMPANY_STORAGE_KEY = 'current_company_id';

interface CompanyContextType {
  companyId: string;
  setCompanyId: (id?: string) => void;
}

const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

export function CompanyProvider({ children }: { children: ReactNode }) {
  const session = useSuperUserHook();
  const { data: userCompaniesData } = useUserCompanies({ userId: session?.data?.userId ?? '' });
  const router = useRouter();

  const [companyId, setCompanyId] = useState<string | undefined>();
  const hasVerifiedSuperToken = useRef(false);

  // Super Token 검증은 한 번만 실행
  useEffect(() => {
    // 이미 검증했거나 세션이 이미 있는 경우 건너뛰기
    if (hasVerifiedSuperToken.current || session?.data) {
      return;
    }

    // POST 요청으로 토큰 검증 (URL 쿼리 파라미터 방식 제거)
    // 쿠키에서 토큰을 읽어서 POST 요청으로 검증
    const handleSuperTokenVerification = async () => {
      const token = getCookie('access_super_token') as string | undefined;

      if (token && token !== 'undefined') {
        hasVerifiedSuperToken.current = true;
        try {
          const response = await verifySuperToken({ token });
          if (response?.Status === ResponseStatus.SUCCESS && response?.Data?.data) {
            // 토큰이 유효하면 이미 쿠키에 설정되어 있으므로 그대로 사용
            setDualStorageValue(COMPANY_STORAGE_KEY, undefined);
            queryClient.invalidateQueries(AuthQueryKey.session());
            router.push('/');
          }
        } catch (error) {
          console.error('Super token verification failed:', error);
          hasVerifiedSuperToken.current = false; // 실패 시 다시 시도 가능하도록
        }
        return;
      }
    };

    handleSuperTokenVerification();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.data]);

  useEffect(() => {
    if (typeof window !== 'undefined' && !companyId && userCompaniesData) {
      const companies = userCompaniesData?.Data?.data?.items?.filter(
        (company) => company.role !== 'BANNED',
      );

      const stored = getDualStorageValue(COMPANY_STORAGE_KEY);

      // 회사가 삭제되어 없어진 case
      if (
        !companies?.length &&
        stored &&
        !router.pathname.includes('/auth') &&
        !router.pathname.includes('/partners') &&
        !router.pathname.includes('/accept/company')
      ) {
        setCompanyId(undefined);
        setDualStorageValue(COMPANY_STORAGE_KEY, undefined);
        router.push('/auth/create-workspace');

        return;
      }

      // 회사가 삭제되어 없어졌지만 멀티워크스페이스 있는 case
      if (
        companies?.length &&
        stored &&
        !companies.find((company) => company.companyId === stored)
      ) {
        setCompanyId(companies[companies.length - 1].companyId);
        return;
      }

      if (
        stored &&
        companies?.length &&
        companies.find((company) => company.companyId === stored)
      ) {
        setCompanyId(stored);
        return;
      }

      const jwtCompanyId = session?.data?.companyId;
      if (jwtCompanyId && companies?.find((c) => c.companyId === jwtCompanyId)) {
        setCompanyId(jwtCompanyId);
        return;
      }

      setCompanyId(companies?.[companies.length - 1]?.companyId);
    }
  }, [session, companyId, userCompaniesData, router]);

  // Persist to localStorage whenever companyId changes
  useEffect(() => {
    if (companyId) {
      setDualStorageValue(COMPANY_STORAGE_KEY, companyId);
    }
  }, [companyId]);

  const handleSetCompanyId = async (id?: string) => {
    await queryClient.invalidateQueries(UserCompaniesQueryKey.default());
    setCompanyId(id);
  };

  return (
    <CompanyContext.Provider
      value={{
        companyId: companyId as string,
        setCompanyId: handleSetCompanyId,
      }}
    >
      {children}
    </CompanyContext.Provider>
  );
}

export function useCompanyContext() {
  const context = useContext(CompanyContext);
  if (context === undefined) {
    throw new Error('useCompanyContext must be used within a CompanyProvider');
  }
  return context;
}
